import { createClient } from '@/shared/api';
import UPDATE_FACILITY_CLINICIAN_CONTACTS_MUTATION from '@/entities/facility/api/mutations/updateFacilityClinicianContacts';
import type { FacilityClinicianContact } from '@/entities/facility/api/services/fetchFacility';
import type {
  UpdateFacilityClinicianContactsMutation,
  UpdateFacilityClinicianContactsMutationVariables,
} from '@/shared/api/generated/graphql';

export type UpdateFacilityClinicianContactsResult = {
  isSuccess: boolean;
  message: string;
  facility: { clinicianContacts: FacilityClinicianContact[] };
};

/**
 * Replaces the clinician contact list attached to a facility. Runs the update mutation, throws the server message on failure, and returns the contacts read back off the updated facility.
 *
 * @param facilityId - Identifier of the facility being updated.
 * @param clinicianContacts - Full replacement list of clinician contacts.
 * @returns A promise resolving to the updated clinician contacts.
 */
const handleUpdateFacilityClinicianContacts = async (
  facilityId: string,
  clinicianContacts: FacilityClinicianContact[],
) => {
  const client = await createClient();
  const updateFacilityClinicianContactsResult = await client.mutate<
    UpdateFacilityClinicianContactsMutation,
    UpdateFacilityClinicianContactsMutationVariables
  >({
    mutation: UPDATE_FACILITY_CLINICIAN_CONTACTS_MUTATION,
    variables: { facilityId, clinicianContacts },
  });
  const { data } = updateFacilityClinicianContactsResult;
  const { updateFacilityClinicianContacts } = data;
  if (!updateFacilityClinicianContacts.isSuccess) {
    throw new Error(updateFacilityClinicianContacts.message);
  }
  return updateFacilityClinicianContacts?.facility?.clinicianContacts;
};

export default handleUpdateFacilityClinicianContacts;
